import { Combobox } from "@base-ui/react/combobox";
import { Field } from "@base-ui/react/field";
import { CheckIcon, ChevronDownIcon, XIcon } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";
import type { FormField } from "@/types/academy";

const FormSelect = ({ block, name }: { block: FormField; name: string }) => {
  const { control } = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const items = block.optionItems.map((item) => ({
          label: item.label,
          value: item.id,
        }));
        const comboboxValue = items.find((i) => i.value === field.value) ?? null;
        return (
          <Field.Root
            className="flex flex-col w-full items-start gap-1"
            dirty={fieldState.isDirty}
            invalid={fieldState.invalid}
            name={block.id}
            touched={fieldState.isTouched}
          >
            <Combobox.Root
              id={block.id}
              items={items}
              onValueChange={(val: { label: string; value: string } | null) =>
                field.onChange(val?.value ?? "")
              }
              value={comboboxValue}
            >
              <div className="relative flex flex-col gap-1 text-sm w-full leading-5 font-bold text-gray-900">
                <label htmlFor={block.id}>{block.label}</label>
                <Combobox.InputGroup className="relative box-content h-10 w-full rounded-md border border-gray-200 bg-[canvas] focus-within:outline-2 focus-within:-outline-offset-1 focus-within:outline-blue-800 [&>input]:pr-[calc(0.5rem+1.5rem)] has-[.combobox-clear]:[&>input]:pr-[calc(0.5rem+1.5rem*2)]">
                  <Combobox.Input
                    className="h-full w-full border-0 bg-transparent pl-3.5 text-base font-normal text-gray-900 outline-none"
                    id={block.id}
                    onBlur={field.onBlur}
                    placeholder={block.placeholder}
                    ref={field.ref}
                  />
                  <div className="absolute right-2 bottom-0 flex h-10 items-center justify-center text-gray-600">
                    <Combobox.Clear
                      aria-label="Seçimi Temizle"
                      className="combobox-clear flex h-10 w-6 items-center justify-center rounded bg-transparent p-0"
                    >
                      <XIcon className="size-4" />
                    </Combobox.Clear>
                    <Combobox.Trigger
                      aria-label="Open popup"
                      className="flex h-10 w-6 items-center justify-center rounded bg-transparent p-0"
                    >
                      <ChevronDownIcon className="size-4" />
                    </Combobox.Trigger>
                  </div>
                </Combobox.InputGroup>
              </div>

              <Combobox.Portal>
                <Combobox.Positioner className="outline-none" sideOffset={4}>
                  <Combobox.Popup className="w-[var(--anchor-width)] max-h-[23rem] max-w-[var(--available-width)] origin-[var(--transform-origin)] rounded-md bg-[canvas] text-gray-900 shadow-lg shadow-gray-200 outline-1 outline-gray-200 transition-[transform,scale,opacity] data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 dark:shadow-none dark:-outline-offset-1 dark:outline-gray-300 duration-100">
                    <Combobox.Empty>
                      <div className="p-4 text-[0.925rem] leading-4 text-gray-600">
                        Bulunamadı.
                      </div>
                    </Combobox.Empty>
                    <Combobox.List className="outline-0 overflow-y-auto scroll-py-[0.5rem] py-2 overscroll-contain max-h-[min(23rem,var(--available-height))] data-[empty]:p-0">
                      {(item: { label: string; value: string }) => (
                        <Combobox.Item
                          className="grid cursor-default grid-cols-[0.75rem_1fr] items-center gap-2 py-2 pr-8 pl-4 text-base leading-4 outline-none select-none data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:text-gray-50 data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-2 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:z-[-1] data-[highlighted]:before:rounded-sm data-[highlighted]:before:bg-gray-900"
                          key={item.value}
                          value={item}
                        >
                          <Combobox.ItemIndicator className="col-start-1">
                            <CheckIcon className="size-3" />
                          </Combobox.ItemIndicator>
                          <span className="col-start-2">{item.label}</span>
                        </Combobox.Item>
                      )}
                    </Combobox.List>
                  </Combobox.Popup>
                </Combobox.Positioner>
              </Combobox.Portal>
            </Combobox.Root>
            <Field.Error
              className="text-sm text-red-600 font-medium"
              match={!!fieldState.error}
            >
              {block.helperText || "Bu alan zorunludur"}
            </Field.Error>

            <Field.Description className="text-sm text-gray-600" />
          </Field.Root>
        );
      }}
      rules={{
        required: block.isRequired ?? "Bu alan zorunludur",
      }}
    />
  );
};

export default FormSelect;
